import React, { useEffect, useState } from 'react';
import { Col, message, Modal, Row } from 'antd';
import { BaseBreadcrumb, BaseBreadcrumbWithRender } from '@app/components/common/BaseBreadcrumb/BaseBreadcrumb';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import Invoice from '@app/components/apps/common/Invoice';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateInvoice } from '@app/api/labs.api';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { MakeInvoicePayment } from '@app/api/finances.api';

const ImportInvoice: React.FC = () => {
  const location = useLocation();
  const { id, pr_code, invoiceId, status, total, moduleType = 'IMPORT' } = location.state || {};
  const [currentBreadcrumName, setCurrentBreadcrumName] = useState<string>('Invoice');
  const { setBreadcrumb, clearHeaderButtons, addHeaderButton } = useHeader();
  const navigate = useNavigate();
  const handleErrorMessages = useApiErrorHandler();
  const [isPayableInvoice, setIsPayableInvoice] = useState<boolean>(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype || 'ADMIN';

  const detailsPath = moduleType === 'EXPORT' ? '/view-export' : '/view-import';

  const IMPORT_ROUTE_CONFIG: Record<string, { path: string; breadcrumbName: string }> = {
    NORMAL: {
      path: '/import/normal',
      breadcrumbName: 'Import - Normal',
    },
    NON_COMPLIANCE_1: {
      path: '/import/non-compliance-1',
      breadcrumbName: 'Import - Non Compliance 1',
    },
    NON_COMPLIANCE_3: {
      path: '/import/non-compliance-3',
      breadcrumbName: 'Import - Non Compliance 3',
    },
    NO_OBJECTION: {
      path: '/import/no-objection',
      breadcrumbName: 'Import - No Objection',
    },
    NON_RELEVANT: {
      path: '/import/non-relevant',
      breadcrumbName: 'Import - Non Relevant',
    },
  };

  const EXPORT_ROUTE_CONFIG: Record<string, { path: string; breadcrumbName: string }> = {
    NORMAL: {
      path: '/export/normal',
      breadcrumbName: 'Export - Normal',
    },
    DRUG: {
      path: '/export/feed-and-drugs',
      breadcrumbName: 'Export - Feed & Drugs',
    },
  };

  const firstRoute = (() => {
    if (moduleType === 'EXPORT') {
      const exportType = localStorage.getItem('exportType') || 'NORMAL';

      return EXPORT_ROUTE_CONFIG[exportType] || EXPORT_ROUTE_CONFIG.NORMAL;
    }

    const importType = localStorage.getItem('selected_import_type') || 'NORMAL';

    return IMPORT_ROUTE_CONFIG[importType] || IMPORT_ROUTE_CONFIG.NORMAL;
  })();

  const routes = [
    {
      path: firstRoute.path,
      breadcrumbName: firstRoute.breadcrumbName,
    },
    {
      path: detailsPath,
      breadcrumbName: pr_code,
      state: { id },
    },
    {
      path: '/view-invoice',
      breadcrumbName: currentBreadcrumName,
    },
  ];

  useEffect(() => {
    console.log('Passed module type:', moduleType);
    setBreadcrumb(<BaseBreadcrumbWithRender routes={routes} separator=">" />);

    addHeaderButton(
      'Download PDF',
      <BaseButton type="primary" size="small" onClick={handleDownloadPDF}>
        Download Invoice
      </BaseButton>,
    );
    if (status === 'PENDING' && (userType === 'CASHIER' || userType === 'CUSTOMER') && isPayableInvoice) {
      addHeaderButton(
        'Register Product',
        <BaseButton type="primary" size="small" onClick={handleMakePayment}>
          Make Payment
        </BaseButton>,
      );
    }
    return () => {
      setBreadcrumb(undefined);
      clearHeaderButtons();
    };
  }, [setBreadcrumb, clearHeaderButtons, addHeaderButton, currentBreadcrumName, moduleType, isPayableInvoice]);

  const handleDownloadPDF = () => {
    const invoiceElement = document.getElementById('invoice-content'); // Target only the Invoice component

    if (!invoiceElement) {
      console.error('Invoice content not found!');
      return;
    }

    html2canvas(invoiceElement, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= 297; // A4 page height in mm

      while (heightLeft > 0) {
        position -= 297;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= 297;
      }

      pdf.save(`invoice-${id}.pdf`);
    });
  };

  const handleMakePayment = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userType = (storedUser?.usertype ?? '').toUpperCase();

    const modalInstance = Modal.info({
      title: 'Select Payment Method',
      content: renderPaymentButtons(),
      okButtonProps: { style: { display: 'none' } },
      maskClosable: true,
      focusTriggerAfterClose: false,
    });

    function renderPaymentButtons() {
      return (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          {userType !== 'CUSTOMER' && (
            <BaseButton
              onClick={() => {
                modalInstance.update({
                  content: renderCashInput(),
                  title: 'Confirm Payment',
                });
              }}
            >
              Cash Payment
            </BaseButton>
          )}
          <BaseButton
            onClick={async () => {
              modalInstance.destroy();
              try {
                await updateInvoice(invoiceId);
                message.success('Card payment confirmed.');
                navigate(detailsPath, { state: { id } });
                // handlePostPaymentStatusChange();
              } catch (err) {
                handleErrorMessages(err);
                console.error('Invoice update error:', err);
              }
            }}
          >
            Card Payment
          </BaseButton>
        </div>
      );
    }

    function renderCashInput() {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
          <p> Pay LKR {total}?</p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <BaseButton
              size="small"
              onClick={() => {
                // Cancel and go back to payment buttons
                modalInstance.update({
                  content: renderPaymentButtons(),
                  title: 'Confirm Payment',
                });
              }}
            >
              Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              size="small"
              onClick={() => {
                if (!total) {
                  message.error('Invoice total undefined');
                  return;
                }

                // Show confirmation modal for entered amount
                Modal.confirm({
                  title: 'Confirm Cash Payment',
                  content: `Pay LKR ${total}?`,
                  okText: 'Confirm & Pay',
                  cancelText: 'Cancel',
                  okButtonProps: { size: 'small' },
                  cancelButtonProps: { size: 'small' },
                  onOk: async () => {
                    modalInstance.destroy();
                    try {
                      await MakeInvoicePayment(invoiceId, 'CASH', total);

                      message.success(`Cash payment of ${total} confirmed.`);
                      navigate(detailsPath, { state: { id } });
                    } catch (err) {
                      handleErrorMessages(err);
                      console.error('Cash payment error:', err);
                    }
                  },
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onCancel: () => {},
                });
              }}
            >
              Pay
            </BaseButton>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Invoice
            id={invoiceId}
            setCurrentBreadcrumName={setCurrentBreadcrumName}
            setIsPayable={setIsPayableInvoice}
          />
        </Col>
      </Row>
    </>
  );
};

export default ImportInvoice;
